import { ReactNode, useEffect } from 'react';
import { app } from '@microsoft/teams-js';
import { Spinner } from '@fluentui/react-components';

import { ApiProvider } from 'src/contexts/ApiContext';
import { TeamsUserProvider } from 'src/contexts/TeamsUserContext';
import { rootStoreInstance } from 'src/stores/RootStore';
import useTeamsFx from 'src/hooks/useTeamsFx';
import { logger } from 'src/logging/Logger';
import { useTeamsUser } from 'src/hooks/useTeamsUser';
import FluentProviderWrapper from './FluentProviderWrapper';
import TeamsFxProvider from './TeamsFxContext';
import ErrorBoundaryProvider from './ErrorBoundaryProvider';
import { StoreProvider } from './RootStoreContext';

const AppReadyGuard = ({ children }: { children: ReactNode }): JSX.Element => {
  const { loading: teamsLoading, user } = useTeamsUser();
  const { loading: teamsFxLoading } = useTeamsFx();

  useEffect(() => {
    if (!teamsLoading && user) {
      rootStoreInstance.teamsUserStore.setUser(user);
      logger.updateUser(user);
    }
  }, [teamsLoading, user]);

  useEffect(() => {
    if (!teamsFxLoading) {
      app.initialize().then(() => {
        app.notifySuccess();
      });
    }
  }, [teamsFxLoading]);

  const isReady = !teamsLoading && !teamsFxLoading && user;
  if (!isReady) {
    return <Spinner />;
  }

  return (
    <StoreProvider>
      <FluentProviderWrapper>
        <ApiProvider>{children}</ApiProvider>
      </FluentProviderWrapper>
    </StoreProvider>
  );
};

const AppProviders = ({ children }: { children: ReactNode }): JSX.Element => (
  <ErrorBoundaryProvider>
    <TeamsFxProvider>
      <TeamsUserProvider>
        <AppReadyGuard>{children}</AppReadyGuard>
      </TeamsUserProvider>
    </TeamsFxProvider>
  </ErrorBoundaryProvider>
);

export default AppProviders;
